import { useEffect, useContext, useMemo, useReducer, useState, memo, useRef, useDeferredValue } from "react";

import { useCostLock } from "../../../hooks/useCostLock";

import {
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    FormGroup,
    Input,
    InputGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import CustomSelect from "../../../components/CustomSelect";
import DataTable from "../../../components/DataTable";
import { useTheme } from "../../../hooks/useTheme";
import Collapsable from "../../../components/Collapsable";
import ItemTypeCustomAttributes from '../../items/components/ItemTypeCustomAttributes';
import { QuoteDataContext } from "../contexts/quoteDataContext";

// -----------------------------
// Actions model & reducer
// -----------------------------

// kinds: set, calc, setBilling
//   set:     { kind: "set", field: string, value: any }                 // set same value across all rows
//   setEach: { kind: "setEach", field: string, byKey: Record<string,number> } // set per-row values by rowKey
//   calc: { kind: "calc", field: "upfrontCost" | "upfrontPrice" | "recurringCost" | "recurringPrice",
//            op: "x" | "/" | "%+" | "%-",
//            source: "Quantity" | "Num Payments" | "Custom",
//            custom?: number }
//   setBilling: { kind: "setBilling", mode: "upfront" | "recurring" }

const initialState = [];

// Sentinel: this field has different values across the selected items
const MIXED = Symbol("MIXED");

function actionsReducer(state, action) {
    switch (action.type) {
        case "add":
            return [...state, action.payload];
        case "replace": // replace or upsert by key (optional)
            return action.payload;
        case "clear":
            return [];
        default:
            return state;
    }
}

// -----------------------------
// Pure helpers
// -----------------------------

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const same = (a, b) => {
    const A = a === undefined ? null : a;
    const B = b === undefined ? null : b;
    return JSON.stringify(A) === JSON.stringify(B);
};

const getSourceValue = (row, source) => {
    if (source === "Quantity") return num(row.quantity);
    if (source === "Num Payments") return num(row.recurringNumPayments ?? row.numPayments);
    return 0; // Custom is handled at call-site
};

// Accepts "", "1", "1.", "1.25", "-.5" etc — good for live typing
const isNumberLike = (s) => /^-?\d*(\.\d*)?$/.test(String(s ?? ""));

// If every row has the same value for `field`, return it (as a string); else ""
const commonNumberString = (rows, field) => {
    if (!rows?.length) return "";
    const first = rows[0]?.[field];
    for (let i = 1; i < rows.length; i++) {
        if (!same(rows[i]?.[field], first)) return "";
    }
    return first ?? "";
};

const applyOp = (base, op, val) => {
    const b = num(base);
    const v = num(val);
    switch (op) {
        case "set":
            return v;
        case "+":
            return b + v;
        case "-":
            return b - v;
        case "x":
            return b * v;
        case "/":
            return v === 0 ? b : b / v;
        case "%+":
            return b * (1 + v / 100);
        case "%-":
            return b * (1 - v / 100);
        case "m%": {
            // margin uplift
            const denom = 1 - v / 100;
            return denom === 0 ? b : b / denom;
        }
        default:
            return b;
    }
};

const parseBoolean = (v) => {
    if (typeof v === "boolean") return v;
    const s = String(v ?? "").trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(s)) return true;
    if (["false", "0", "no", "n", "off", ""].includes(s)) return false;
    return !!v; // final fallback
};

const isBooleanField = (fieldType) => {
    const ft = String(fieldType ?? "").trim().toLowerCase();
    return ["boolean", "bool", "checkbox", "toggle", "switch"].includes(ft);
};

const toYesNo = (v) => (parseBoolean(v) ? "Yes" : "No");
const fromYesNo = (v) => parseBoolean(v); // accepts Yes/No/true/false/on/off/etc → boolean

const keyOf = (row, i) => row.id ?? row.itemId ?? row.itemLookupId ?? row._id ?? i;

// A small projection for the preview table
const toFriendly = (it, i, useFallbacks = false) => {
    const out = {
        _rowKey: keyOf(it, i),
        id: it.id ?? it.itemId ?? it.itemLookupId ?? it._id,
        name: it.internalName ?? it.name ?? it.description ?? it._linkItem?.name ?? it.internalName ?? null,
        description: it.description ?? it.name ?? it.internalName,
        supplier: it.supplier ?? it.supplierName ?? it._linkItem?.supplier ?? it._linkItem?.supplierName,
        itemType: it.itemType ?? it.typeName ?? it.itemLookupTypeName ?? it._linkItem?.itemLookupTypeName,
        quantity: it.quantity,
        billing: it.isUpfront && it.isRecurring ? "Both" : it.isUpfront ? "Upfront" : it.isRecurring ? "Recurring" : null,
        upfrontRecognitionMonths: it.upfrontRecognitionMonths ?? null,
        upfrontCost: useFallbacks ? it.upfrontCost ?? it.cost ?? null : it.upfrontCost ?? null,
        upfrontPrice: useFallbacks ? it.upfrontPrice ?? it.price ?? null : it.upfrontPrice ?? null,
        recurringCost: it.recurringCost ?? null,
        recurringPrice: it.recurringPrice ?? null,
        upfrontDescription: it.upfrontDescription ?? it.upfrontNameOnQuote ?? null,
        recurringDescription: it.recurringDescription ?? it.recurringNameOnQuote ?? null,
        autotaskCode: it.autotaskCode ?? null,
        upfrontSalesforceCode: it.upfrontSalesforceCode ?? null,
        recurringSalesforceCode: it.recurringSalesforceCode ?? null,
        recurringStartMonth: it.recurringStartMonth,
        recurringIntervalMonths: it.recurringIntervalMonths,
        recurringNumPayments: it.recurringNumPayments,
        isInternal: it?.isInternal ? "Yes" : "No",
        isCreditItem: it?.isCreditItem ? "Yes" : "No",
        groupName: it?.groupName ?? null,
        quoteGroupDescription: it?.quoteGroupDescription ?? null
    };
    // Flatten type custom attributes so they can appear in the table & diff
    const attrs = Array.isArray(it.typeCustomAttributes) ? it.typeCustomAttributes : [];
    for (const a of attrs) {
        const label = String(a?.label ?? a?.name ?? a?.key ?? "").trim();
        if (!label) continue;
        out[`attr::${label}`] = a?.value ?? null;
    }
    return out;
};

// pick first non-null/undefined/non-empty and non-zero value, else 0
const pickVisible = (...vals) => {
    for (const v of vals) {
        if (v === null || v === undefined || v === "") continue;
        if (typeof v === "number" && v === 0) continue;
        return v;
    }
    return 0;
};

function clone(item) {
    return JSON.parse(JSON.stringify(item ?? {}));
}

// Group items by type actually present in the selection, and attach the matching type definition
function groupItemsByType(items = [], itemTypes = []) {
    const byKey = new Map();
    const typeDefById = new Map(itemTypes.map(t => [String(t.id), t]));
    const typeDefByName = new Map(
        itemTypes.map(t => [String(t.itemLookupTypeName ?? t.name ?? "").trim().toLowerCase(), t])
    );

    for (const it of items) {
        const id = it.itemLookupTypeId ?? it.itemTypeId ?? it.typeId ?? null;
        const name = it.itemType ?? it.typeName ?? it.itemLookupTypeName ?? null;

        const key = id != null ? String(id) : String(name ?? "");
        if (!byKey.has(key)) {
            const def =
                id != null
                    ? typeDefById.get(String(id))
                    : typeDefByName.get(String(String(name ?? "").trim().toLowerCase()));
            byKey.set(key, {
                key,
                typeId: id ?? null,
                typeName: name ?? (def?.itemLookupTypeName ?? def?.name ?? "Unknown Type"),
                def,
                rows: [],
            });
        }
        byKey.get(key).rows.push(it);
    }

    return Array.from(byKey.values());
}

// Fields that should only be applied to recurring/upfront items respectively
const RECURRING_FIELDS = ["recurringCost", "recurringPrice", "recurringDescription", "recurringNameOnQuote", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments", "recurringSalesforceCode"];
const UPFRONT_FIELDS = ["upfrontCost", "upfrontPrice", "upfrontDescription", "upfrontNameOnQuote", "upfrontSalesforceCode", "upfrontRecognitionMonths"];

// The heart: apply a list of user actions to the original items
function applyActions(items, actions) {
    const rows = items.map((it) => clone(it));

    for (const a of actions) {
        if (!a) continue;

        // Set a type custom attribute (by label) for rows of a specific type
        if (a.kind === "setTypeAttr") {
            const { typeKey, attrLabel, value } = a;
            const labelLc = String(attrLabel || "").trim().toLowerCase();
            if (!labelLc) continue;

            for (const r of rows) {
                const rowTypeId = r.itemLookupTypeId ?? r.itemTypeId ?? r.typeId ?? null;
                const rowTypeName = r.itemType ?? r.typeName ?? r.itemLookupTypeName ?? null;
                const rowKey = rowTypeId != null ? String(rowTypeId) : String(rowTypeName || "");
                if (String(typeKey) !== String(rowKey)) continue;

                const arr = Array.isArray(r.typeCustomAttributes)
                    ? [...r.typeCustomAttributes]
                    : [];
                let found = false;
                for (let i = 0; i < arr.length; i++) {
                    const lbl = String(arr[i]?.label ?? arr[i]?.name ?? "").trim();
                    if (lbl.toLowerCase() === labelLc) {
                        arr[i] = { ...arr[i], label: lbl, value };
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    arr.push({ label: attrLabel, value });
                }
                r.typeCustomAttributes = arr;

                // ✅ Mark as user edited so import merge won't overwrite it
                r._userEdited = { ...(r._userEdited || {}), typeCustomAttributes: true };
            }
            continue;
        }


        if (a.kind === "set") {
            for (const r of rows) {
                // supports toggling flags and direct fields
                if (RECURRING_FIELDS.includes(a.field) && !r.isRecurring) continue;
                if (UPFRONT_FIELDS.includes(a.field) && !r.isUpfront) continue;

                r[a.field] = a.value;
            }
            continue;
        }

        if (a.kind === "setEach") {
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];

                if (RECURRING_FIELDS.includes(a.field) && !r.isRecurring) continue;
                if (UPFRONT_FIELDS.includes(a.field) && !r.isUpfront) continue;

                console.log("setEach", a.field, "rowKey:", keyOf(r, i), "value:", a.byKey[keyOf(r, i)]);

                const key = keyOf(r, i);
                if (a.byKey && Object.prototype.hasOwnProperty.call(a.byKey, key)) {
                    r[a.field] = a.byKey[key];
                }
            }
            continue;
        }

        if (a.kind === "calc") {
            const { field, op, source, custom } = a;
            for (const r of rows) {
                if (RECURRING_FIELDS.includes(field) && !r.isRecurring) continue;
                if (UPFRONT_FIELDS.includes(field) && !r.isUpfront) continue;

                const base =
                    r[field] ??
                    (field.startsWith("upfront")
                        ? r[field.replace("upfront", "").toLowerCase()] // upfrontCost -> cost, upfrontPrice -> price
                        : field.startsWith("recurring")
                            ? r[field.replace("recurring", "").toLowerCase()] // recurringCost -> cost, recurringPrice -> price
                            : 0);
                const src = op === "m%" ? num(custom) : source === "Custom" ? num(custom) : getSourceValue(r, source);
                r[field] = applyOp(base, op, src);
            }
            continue;
        }

        if (a.kind === "resetPricing") {
            // Restore the field to the value it had in the original items (i.e. when the modal opened).
            // rows[i] was cloned from items[i], so the indices align exactly.
            const { field } = a;
            for (let i = 0; i < rows.length; i++) {
                rows[i][field] = items[i]?.[field] ?? null;
            }
            continue;
        }

        if (a.kind === "setBilling") {
            for (const r of rows) {
                if (a.mode === "recurring") {
                    // choose visible pricing → recurring; clear upfront to 0
                    const movedCost = pickVisible(r.recurringCost, r.upfrontCost, r.cost);
                    const movedPrice = pickVisible(r.recurringPrice, r.upfrontPrice, r.price);

                    r.recurringCost = num(movedCost);
                    r.recurringPrice = num(movedPrice);
                    r.upfrontCost = 0;
                    r.upfrontPrice = 0;

                    // MOVE QUOTE DESCRIPTION → Recurring; clear upfront
                    const movedDesc = pickVisible(
                        r.recurringDescription,
                        r.upfrontDescription,
                        r.description,
                        r.name
                    );
                    r.recurringDescription = movedDesc ?? null;
                    r.upfrontDescription = null;

                    r.isUpfront = false;
                    r.isRecurring = true;
                } else if (a.mode === "upfront") {
                    // choose visible pricing → upfront; clear recurring to 0
                    const movedCost = pickVisible(r.upfrontCost, r.recurringCost, r.cost);
                    const movedPrice = pickVisible(r.upfrontPrice, r.recurringPrice, r.price);

                    r.upfrontCost = num(movedCost);
                    r.upfrontPrice = num(movedPrice);
                    r.recurringCost = 0;
                    r.recurringPrice = 0;

                    // MOVE QUOTE DESCRIPTION → Upfront; clear recurring
                    const movedDesc = pickVisible(
                        r.upfrontDescription,
                        r.recurringDescription,
                        r.description,
                        r.name
                    );
                    r.upfrontDescription = movedDesc ?? null;
                    r.recurringDescription = null;

                    r.isUpfront = true;
                    r.isRecurring = false;
                }
            }
            continue;
        }

    }

    // Re-derive salesforceCode from upfront/recurring codes
    for (const r of rows) {
        const upCode = r.isUpfront ? (r.upfrontSalesforceCode || null) : null;
        const recCode = r.isRecurring ? (r.recurringSalesforceCode || null) : null;
        if (upCode && recCode && upCode !== recCode) {
            r.salesforceCode = `${upCode} / ${recCode}`;
        } else {
            r.salesforceCode = upCode || recCode || null;
        }
    }

    return rows;
}

// Compute diff styling vs original
function makeDiffHelpers(originalItems, currentItems) {
    const BILLING_KEYS = new Set(["upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice"]);

    const originalWithFallbacks = new Map();
    const originalRaw = new Map();
    const currentRaw = new Map();

    originalItems.forEach((r, i) => {
        originalWithFallbacks.set(keyOf(r, i), toFriendly(r, i, /* useFallbacks */ true));
        originalRaw.set(keyOf(r, i), toFriendly(r, i, /* useFallbacks */ false));
    });
    (currentItems || []).forEach((r, i) => {
        currentRaw.set(keyOf(r, i), toFriendly(r, i, /* useFallbacks */ false));
    });

    const normalize = (v) => {
        if (v === "" || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) && v !== null && typeof v !== "boolean" ? n : v;
    };

    const hasChanged = (row, colKey) => {
        const withFallbacks = originalWithFallbacks.get(row._rowKey);
        const rawOrig = originalRaw.get(row._rowKey);
        const rawCurr = currentRaw.get(row._rowKey);

        if (!withFallbacks || !rawOrig) return false;

        if (BILLING_KEYS.has(colKey)) {
            // Billing: compare RAW current vs RAW original (avoid fallback-driven false positives)
            const currVal = rawCurr ? rawCurr[colKey] : undefined;
            const origVal = rawOrig[colKey];
            return JSON.stringify(normalize(currVal)) !== JSON.stringify(normalize(origVal));
        }

        // Others: compare preview (with fallbacks) vs original (with fallbacks)
        return JSON.stringify(normalize(row[colKey])) !== JSON.stringify(normalize(withFallbacks[colKey]));
    };

    const getCellClassName = (row, colKey) => (hasChanged(row, colKey) ? "text-success fw-semibold" : "");
    return { hasChanged, getCellClassName };
}

// Columns derived from preview contents (only show populated fields)
function autoColumns(preview, hasChanged) {
    if (!preview?.length) return [];

    const preferred = [
        "name",
        "quantity",
        "billing",
        "upfrontCost",
        "upfrontPrice",
        "recurringCost",
        "recurringPrice",
        "upfrontDescription",
        "upfrontRecognitionMonths",
        "recurringDescription",
        "autotaskCode",
        "upfrontSalesforceCode",
        "recurringSalesforceCode",
        "supplier",
        "itemType",
        "groupName",
        "quoteGroupDescription",
        "recurringStartMonth",
        "recurringIntervalMonths",
        "recurringNumPayments",
        "isInternal",
        "isCreditItem"
    ];

    const exists = new Set();

    // Ensure the 4 billing columns behave exactly as requested
    const important = ["upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice"];
    const isMeaningful = (v) => {
        const isNullish = v === null || v === undefined || v === "";
        const isZero = typeof v === "number" && v === 0;
        return !isNullish && !isZero;
    };

    for (const key of important) {
        const anyMeaningful = preview.some((r) => isMeaningful(r[key]));
        const anyChanged = typeof hasChanged === "function" && preview.some((r) => hasChanged(r, key));
        if (anyMeaningful || anyChanged) exists.add(key);
    }

    // Add all other columns if they have meaningful values or changed (even to 0/null)
    for (const r of preview) {
        for (const [k, v] of Object.entries(r)) {
            if (k.startsWith("_")) continue; // hide _rowKey/meta
            if (important.includes(k)) continue; // already decided above

            const isNullish = v === null || v === undefined || v === "";
            const isZero = typeof v === "number" && v === 0;

            if ((!isNullish && !isZero) || (hasChanged && hasChanged(r, k))) {
                exists.add(k);
            }
        }
    }

    const order = [
        ...preferred.filter((k) => exists.has(k)),
        ...Array.from(exists).filter((k) => !preferred.includes(k)),
    ];

    const isCurrencyKey = (k) => {
        const lc = k.toLowerCase();
        return lc.includes("cost") || lc.includes("price");
    };
    const formatCurrency = (v) => {
        if (v === null || v === undefined || v === "") return "";
        const n = Number(v);
        if (!Number.isFinite(n)) return v;
        return "£" + n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return order.map((key) => ({
        key,
        label: key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()),
        ...(isCurrencyKey(key) ? { render: (val) => formatCurrency(val), align: "right" } : {}),
    }));
}

// Small helper for adding/overwriting a set action
function setAction(dispatch, field, value) {
    dispatch({ type: "add", payload: { kind: "set", field, value } });
}

function calcAction(dispatch, field, op, source, custom) {
    dispatch({ type: "add", payload: { kind: "calc", field, op, source, custom } });
}

function setBillingAction(dispatch, mode) {
    dispatch({ type: "add", payload: { kind: "setBilling", mode } });
}

const firstNonNil = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && v !== "") ?? null;

// -----------------------------
// Checkbox that renders indeterminate when items have mixed values
// -----------------------------
const IndeterminateCheckbox = ({ id, mixed, defaultChecked, onChange, className, disabled }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current) return;
        ref.current.indeterminate = !!mixed;
        if (!mixed) ref.current.checked = !!defaultChecked;
    }, [mixed, defaultChecked]);
    return (
        <input
            ref={ref}
            id={id}
            type="checkbox"
            defaultChecked={!mixed && !!defaultChecked}
            onChange={onChange}
            className={"form-check-input " + (className ?? "")}
            style={mixed ? { 
                        backgroundColor: 'var(--bs-warning)',
                        borderColor: 'var(--bs-warning)'
                    } : {}}
            disabled={disabled}
        />
    );
};

// Ops that use a direct number input (vs the Quantity/NumPayments/Custom selectbox)
const getModifierInputType = (op) => ["m%", "%+", "%-", "+", "-", "set"].includes(op) ? "number" : "selectbox";

const PREDEFINED_SOURCES = ["Quantity", "Num Payments"];

// -----------------------------
// Combo text input + dropdown that lets the user either type a number
// or pick a named source (Quantity / Num Payments).
// Calls onChange(source, customValue) whenever the value changes.
// Remount (via key) to reset.
// -----------------------------
const NumberOrSourceInput = memo(function NumberOrSourceInput({ isLocked, onChange }) {
    const [value, setValue] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef(null);

    const effectiveSource = PREDEFINED_SOURCES.includes(value) ? value : "Custom";
    const effectiveCustom = parseFloat(value);

    useEffect(() => {
        onChange?.(effectiveSource, effectiveCustom);
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <InputGroup style={{flexWrap: "nowrap", width: "auto"}}>
            <Input
                type="text"
                inputMode={PREDEFINED_SOURCES.includes(value) ? "none" : "decimal"}
                innerRef={inputRef}
                readOnly={PREDEFINED_SOURCES.includes(value)}
                className={(isLocked ? "locked" : "")}
                style={{minWidth: "120px", maxWidth: "120px"}}
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    if (isNumberLike(v)) setValue(v);
                }}
                placeholder="(Enter Value)"
            />
            <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen((o) => !o)}>
                <DropdownToggle
                    tag="button"
                    type="button"
                    className={"form-control" + (isLocked ? " locked" : "")}
                    style={{
                        width: "auto",
                        flexShrink: 0,
                        borderLeft: "none",
                        borderRadius: "0 var(--bs-border-radius, 0.375rem) var(--bs-border-radius, 0.375rem) 0",
                        padding: "0.375rem 0.6rem",
                        cursor: "pointer",
                        fontSize: "1rem",
                        lineHeight: 1,
                    }}
                >▾</DropdownToggle>
                <DropdownMenu end>
                    <DropdownItem onClick={() => { setValue(""); setTimeout(() => inputRef.current?.focus(), 0); }}>Enter Value</DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem onClick={() => setValue("Quantity")}>Quantity</DropdownItem>
                    <DropdownItem onClick={() => setValue("Num Payments")}>Num Payments</DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </InputGroup>
    );
});

// -----------------------------
// Top-level CalcRow (stable identity)
// -----------------------------
const CalcRow = memo(function CalcRow({
    label,
    field,
    disabled = false,
    setField, // (field, value) => void
    applyCalcOnce, // (field, op, source, custom) => void
    reset,
    isLocked
}) {
    const [op, setOp] = useState("set");
    const [source, setSource] = useState("Custom");
    const [customValue, setCustomValue] = useState(""); // keep as string for live typing (number-mode ops)

    const modifierInputType = useMemo(() => getModifierInputType(op), [op]);

    // reset the value if op changes
    useEffect(() => {
        if (op === "m%") {
            setCustomValue(40); // 40% margin is a common starting point);
        } else {
            setCustomValue("");
        }
    }, [op]);

    const applyIsDisabled = isLocked || (modifierInputType === "number" || source === "Custom"
                                ? !Number.isFinite(parseFloat(customValue))
                                : false);

    return (
        <Row className="align-items-center g-2">
            <Col sm={2}>
                <Label className="mt-2">{label}</Label>
            </Col>


            <Col sm="auto">
                <InputGroup row>
                    <Input type="select" value={op} onChange={(e) => setOp(e.target.value)} 
                        className={(isLocked ? " locked" : "")}
                        style={{flex: "0 0 auto", width: "210px"}}
                    >
                        <option value="set">Set New Value</option>
                        <option value="+">Increase current value by £</option>
                        <option value="-">Decrease current value by £</option>
                        <option value="%+">Increase current value by %</option>
                        <option value="%-">Decrease current value by %</option>
                        <option value="x">Multiply current value by</option>
                        <option value="/">Divide current value by</option>
                        {field.toLocaleLowerCase().includes("price") && <option value="m%">Set from cost with % margin</option>}
                    </Input>
                </InputGroup>
            </Col>
            <Col>
                <InputGroup row>
                    {modifierInputType === "selectbox" && (
                        <NumberOrSourceInput
                            key={`${op}`} // When op changes, we want to reset the inputs.  Assigning new key will remount it and reset it
                            isLocked={isLocked}
                            onChange={(src, cust) => { setSource(src); setCustomValue(cust); }}
                        />
                    )}

                    {modifierInputType === "number" && (
                        <Input
                            className={isLocked ? "locked" : ""}
                            style={{minWidth: "128px", maxWidth: "128px", flex: "0 0 auto" }}
                            type="text"
                            inputMode="decimal"
                            value={customValue}
                            onChange={(e) => { if (isNumberLike(e.target.value)) setCustomValue(e.target.value); }}
                            placeholder={op === "m%" 
                                            ? "(Enter Margin %)" 
                                            : op.includes("%")
                                                ? "(Enter Percentage)"
                                                : "(Enter Value)"}
                        />
                    )}
                </InputGroup>
            </Col>
            <Col sm="auto">
                <div>
                    <Button
                        className={"mx-2" + (isLocked ? " locked" : "")}
                        outline
                        disabled={applyIsDisabled}
                        onClick={() => {
                            const customNum = parseFloat(customValue);
                            applyCalcOnce(field, op, source, Number.isFinite(customNum) ? customNum : 0); // one-shot calc → setEach
                        }}
                    >
                        Apply
                    </Button>
                    <Button 
                        outline 
                        disabled={disabled || isLocked} 
                        onClick={() => { reset(); setSource("Custom"); 
                        setCustomValue(""); }} 
                        title="Resets to the value when the modal was opened. Does NOT undo user edits prior to this">
                        Reset
                    </Button>
                </div>
            </Col>
        </Row>
    );
});

// -----------------------------
// UI
// -----------------------------

export default function QuoteItemBulkModal({
    modalOpen,
    setModalOpen,
    items = [],
    suppliers = [],
    itemTypes = [],
    groupNames = [],
    overrideGroupNames,
    isQuoteImport = false,
    onSave,
    showDetailsTab = true,
    showConfigTab = true,
    showCommercialsTab = true,
    showImportTab = false,
    allowSetBillingType = false, // if true, show "Move All Recurring To Upfront" and "Move All Upfront To Recurring" buttons that move values between upfront/recurring and set the billing flags accordingly
}) {
    const theme = useTheme();

    const { salesforceCodes } = useContext(QuoteDataContext)

    // I will use this for all cost fields (upfrontCost, recurringCost) since they should be locked together
    // basically if I pass it a string with word "cost" it will return true if the user has permissions to
    // edit costs, and false if they don't - then I can use that to disable inputs and add a "locked" 
    // class for styling
    const isCostLocked = useCostLock("cost");

    // For each field: the single common value across all items, or MIXED if they differ
    const commonStartValues = useMemo(() => {
        if (!items?.length) return {};
        const getCommon = (getVal) => {
            const vals = items.map(getVal);
            const first = vals[0];
            return vals.every(v => same(v, first)) ? (first ?? null) : MIXED;
        };
        return {
            name:                    getCommon(it => it.internalName ?? it.name ?? it.description ?? null),
            autotaskCode:            getCommon(it => it.autotaskCode ?? null),
            quantity:                getCommon(it => it.quantity ?? null),
            supplierId:              getCommon(it => it.supplierId ?? null),
            itemLookupTypeId:        getCommon(it => it.itemLookupTypeId ?? null),
            upfrontDescription:      getCommon(it => it.upfrontDescription ?? it.upfrontNameOnQuote ?? null),
            recurringDescription:    getCommon(it => it.recurringDescription ?? it.recurringNameOnQuote ?? null),
            recurringStartMonth:     getCommon(it => it.recurringStartMonth ?? null),
            recurringIntervalMonths: getCommon(it => it.recurringIntervalMonths ?? null),
            upfrontRecognitionMonths: getCommon(it => it.upfrontRecognitionMonths ?? null),
            recurringNumPayments:    getCommon(it => it.recurringNumPayments ?? null),
            isInternal:              getCommon(it => !!it.isInternal),
            isCreditItem:            getCommon(it => !!it.isCreditItem),
            upfrontSalesforceCode:   getCommon(it => it.upfrontSalesforceCode ?? null),
            recurringSalesforceCode: getCommon(it => it.recurringSalesforceCode ?? null),
            quoteGroupDescription:   getCommon(it => it.quoteGroupDescription ?? null),
        };
    }, [items]);

    const [actions, dispatch] = useReducer(actionsReducer, initialState);
    // Defer the expensive applyActions/toFriendly/makeDiffHelpers chain so keystrokes
    // remain instant even with 1000+ items. Inputs use defaultValue (uncontrolled) so
    // the DOM updates immediately; only the preview table lags a render behind.
    const deferredActions = useDeferredValue(actions);

    // salesforceCodes() returns static data — compute once per type
    const upfrontSfCodeOptions = useMemo(() =>
        (salesforceCodes || [])
            .filter(c => c.type === "Upfront" || (c.type === "Recurring" && c.canBePaidUpfront))
            .map(c => ({ value: c.name, 
                label: c.type === "Upfront" ? c.label ?? c.name : c.name + " 🔁",                          
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    []);
    const recurringSfCodeOptions = useMemo(() =>
        (salesforceCodes || [])
            .filter(c => c.type === "Recurring")
            .map(c => ({ value: c.name, label: c.label ?? c.name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
    []);

    const tabs = useMemo(() => {
        const t = [];
        if (showImportTab) t.push("Import");
        if (showDetailsTab) t.push("Details");
        if (showCommercialsTab) t.push("Commercials");
        if (showConfigTab) t.push("Configuration");
        return t;
    }, [showDetailsTab, showCommercialsTab, showConfigTab, showImportTab]);

    //tab
    const [currentActiveTab, setCurrentActiveTab] = useState(tabs[0] || "Details");

    // Key incremented each time the modal opens — forces uncontrolled inputs to remount with fresh defaultValues
    const [formKey, setFormKey] = useState(0);

    // Controlled values for the Supplier and Type selects (reset on modal open)
    const [supplierIdSelected, setSupplierIdSelected] = useState(null);
    const [typeIdSelected, setTypeIdSelected] = useState(null);

    //config
    const [configDraft, setConfigDraft] = useState({});
    
    // new group name states
    const [newGroupName, setNewGroupName] = useState("");
    const [isCreatingGroupName, setIsCreatingGroupName] = useState(false);
    const newGroupNameInputRef = useRef(null);

    // Run a calculator once and store the concrete per-row results.
    const applyCalcOnce = (field, op, source, custom) => {
        // Start from current preview so calculators are applied on top of any prior edits
        const baseRows = applyActions(items, actions);
        const byKey = {};
        for (let i = 0; i < baseRows.length; i++) {
            const r = baseRows[i];
            // For margin%, the base must be the corresponding COST field (price = cost / (1 - margin/100)),
            // not the price itself. Use the post-action cost so prior edits are reflected.
            const base = op === "m%"
                ? num(field === "upfrontPrice"   ? (r.upfrontCost   ?? r.cost ?? 0)
                    : field === "recurringPrice" ? (r.recurringCost ?? r.cost ?? 0)
                    : r[field])
                : r[field] ??
                    (field.startsWith("upfront")
                        ? r[field.replace("upfront", "").toLowerCase()]
                        : field.startsWith("recurring")
                            ? r[field.replace("recurring", "").toLowerCase()]
                            : 0);
            const src = getModifierInputType(op) === "number" ? num(custom) : source === "Custom" ? num(custom) : getSourceValue(r, source);
            byKey[keyOf(r, i)] = applyOp(base, op, src);
        }
        // Append setEach so it overrides prior sets/calcs; no calc action is stored
        dispatch({ type: "add", payload: { kind: "setEach", field, byKey } });
    };

    // Build select options
    const supplierOptions = useMemo(() => {
        const opts = (suppliers || []).map((s) => ({
            value: s.id ?? s.value,
            label: s.supplierName ?? s.name ?? s.label ?? "",
        }));
        return opts
            .filter((o) => o.value != null && o.label.trim() !== "")
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [suppliers]);

    const typeOptions = useMemo(() => {
        const opts = (itemTypes || []).map((t) => ({
            value: t.id ?? t.value,
            label: t.itemLookupTypeName ?? t.name ?? t.label ?? "",
        }));
        return opts
            .filter((o) => o.value != null && o.label.trim() !== "")
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [itemTypes]);

    const containsNonFullyEditableItems = useMemo(() => {
        return items.some(it => ["basket processing"].includes(it.source?.toLocaleLowerCase()) || (it.quoteModuleGuid != null && it.quoteModuleGuid !== ""));
    }, [items]);

    const groupOptions = useMemo(() => {
        const opts = (groupNames || []).map((g) => ({
            value: g,
            label: g,
        }));
        return opts
            .filter((o) => o.value != null && o.label.trim() !== "")
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [groupNames]);

    // Derived preview
    const previewRaw = useMemo(() => applyActions(items, deferredActions), [items, deferredActions]);
    const preview = useMemo(() => previewRaw.map((it, i) => toFriendly(it, i, true)), [previewRaw]);

    // If every row shares the same upfront/recurring SF code, show it selected; else blank.
    const commonUpfrontSalesforceCode = useMemo(
        () => commonNumberString(previewRaw.filter(x => x.isUpfront), "upfrontSalesforceCode") || undefined,
        [previewRaw]
    );
    const commonRecurringSalesforceCode = useMemo(
        () => commonNumberString(previewRaw.filter(x => x.isRecurring), "recurringSalesforceCode") || undefined,
        [previewRaw]
    );

    // UPDATED: pass previewRaw (current raw rows) to makeDiffHelpers
    const { hasChanged, getCellClassName } = useMemo(
        () => makeDiffHelpers(items, previewRaw),
        [items, previewRaw]
    );

    const columns = useMemo(() => autoColumns(preview, hasChanged), [preview, hasChanged]);

    // Lightweight local UI state mirrors by adding actions
    const onText = (field) => (e) => setAction(dispatch, field, e.target.value === "" ? null : e.target.value);
    const onNumber = (field) => (e) =>
        setAction(dispatch, field, e.target.value === "" ? null : Number(e.target.value));
    const onCheck = (field) => (e) => setAction(dispatch, field, !!e.target.checked);

    // stable function to pass to CalcRow
    const setField = (field, value) => setAction(dispatch, field, value);

    // Reset all form state when modal opens fresh
    useEffect(() => {
        if (modalOpen) {
            dispatch({ type: "clear" });
            setConfigDraft({});
            setFormKey(k => k + 1);
            // Derive initial select values: common ID across all items, or null if mixed
            const getCommonRaw = (getVal) => {
                if (!items?.length) return null;
                const vals = items.map(getVal);
                return vals.every(v => same(v, vals[0])) ? (vals[0] ?? null) : null;
            };
            setSupplierIdSelected(getCommonRaw(it => it.supplierId ?? null));
            setTypeIdSelected(getCommonRaw(it => it.itemLookupTypeId ?? null));
        }
    }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const [groupedItemsByType, setGroupedItemsByType] = useState(groupItemsByType(items, itemTypes));
    useEffect(() => {
        setGroupedItemsByType(groupItemsByType(previewRaw, itemTypes));
    }, [previewRaw, itemTypes]);

    useEffect(() => {
        setCurrentActiveTab(tabs[0] || "Details");
    }, [modalOpen, tabs]);

    const numRecurringItems = useMemo(() => preview.filter(it => it.billing === "Recurring" || it.billing === "Both").length, [preview]);
    const numUpfrontItems = useMemo(() => preview.filter(it => it.billing === "Upfront" || it.billing === "Both").length, [preview]);

    const createGroupName = () => {
        if (newGroupName.trim() === "" || !overrideGroupNames) return;
        
        setNewGroupName("");
        setIsCreatingGroupName(false);

        if (!groupNames.includes(newGroupName.trim()))
            overrideGroupNames([...groupNames, newGroupName.trim()]);
    };

    return (
        <Modal
            isOpen={modalOpen}
            toggle={() => setModalOpen(!modalOpen)}
            centered
            size="xl"
            fullscreen
            scrollable
            backdrop="static"
        >
            <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                Bulk update {items.length === 1 ? items[0]?.description ?? items[0]?.name ?? "item" : `${items.length} items`}
            </ModalHeader>
            <ModalBody>
                <Container fluid key={formKey}>
                    <Row>
                        <Col style={{ display: 'inline-flex' }}>
                            {tabs.map((tab) => (
                                <h6
                                    key={tab}
                                    className={currentActiveTab === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setCurrentActiveTab(tab)}
                                >
                                    {tab}
                                </h6>
                            ))}
                        </Col>
                    </Row>

                    {(currentActiveTab === 'Details' || currentActiveTab === 'Commercials') && containsNonFullyEditableItems && (<>
                        <div className="alert alert-warning">
                            Some fields cannot be bulk edited as your selection includes items sourced from Basket Processing or Modules. Please edit these items individually if you wish to change those fields.
                        </div>
                    </>)}

                    {(currentActiveTab === 'Details' || currentActiveTab === 'Import') && (<>

                        {/* Basic fields */}
                        <Row className="g-5">
                            <Col md={8}>
                                <FormGroup row>
                                    <Label for="name" sm={2}>
                                        Name
                                    </Label>
                                    <Col sm={10}>
                                        <Input id="name"
                                            type="text"
                                            disabled={containsNonFullyEditableItems}
                                            defaultValue={commonStartValues.name !== MIXED ? (commonStartValues.name ?? "") : ""}
                                            placeholder={commonStartValues.name === MIXED ? "Multiple Values" : ""}
                                            onChange={(e) => {
                                                onText("name")(e);
                                                onText("internalName")(e);
                                            }}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row>
                                    <Label for="autotaskCode" sm={2}>
                                        Autotask Code
                                    </Label>
                                    <Col sm={10}>
                                        <Input id="autotaskCode"
                                            type="text"
                                            disabled={containsNonFullyEditableItems}
                                            defaultValue={commonStartValues.autotaskCode !== MIXED ? (commonStartValues.autotaskCode ?? "") : ""}
                                            placeholder={commonStartValues.autotaskCode === MIXED ? "Multiple Values" : ""}
                                            onChange={onText("autotaskCode")}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row>
                                    <Label for="quantity" sm={2}>
                                        Quantity
                                    </Label>
                                    <Col sm={2}>
                                        <Input id="quantity"
                                            type="number"
                                            defaultValue={commonStartValues.quantity !== MIXED ? (commonStartValues.quantity ?? "") : ""}
                                            onChange={onNumber("quantity")}
                                            disabled={containsNonFullyEditableItems}
                                        />
                                    </Col>
                                </FormGroup>                                
                            </Col>
                            <Col md={4}>
                                <FormGroup row className="align-items-center">
                                    <Label sm={4} className="col-form-label" htmlFor="supplier">
                                        Supplier
                                    </Label>
                                    <Col sm={8}>
                                        <CustomSelect
                                            inputId="supplier"
                                            isSearchable
                                            options={supplierOptions}
                                            value={supplierIdSelected}
                                            onChange={(opt) => {
                                                setSupplierIdSelected(opt?.value ?? null);
                                                setAction(dispatch, "supplier", opt?.label ?? null);
                                                setAction(dispatch, "supplierId", opt?.value ?? null);
                                            }}
                                            isDisabled={containsNonFullyEditableItems}
                                            placeholder={commonStartValues.supplierId === MIXED ? "— Multiple Values —" : "— Select supplier —"}
                                        />
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="align-items-center mt-2">
                                    <Label sm={4} className="col-form-label" htmlFor="itemType">
                                        Type
                                    </Label>
                                    <Col sm={8}>
                                        <CustomSelect
                                            inputId="itemType"
                                            isSearchable
                                            options={typeOptions}
                                            value={typeIdSelected}
                                            isDisabled={containsNonFullyEditableItems}
                                            onChange={(opt) => {
                                                setTypeIdSelected(opt?.value ?? null);
                                                setAction(dispatch, "itemType", opt?.label ?? null);
                                                setAction(dispatch, "itemLookupTypeId", opt?.value ?? null);
                                            }}
                                            placeholder={commonStartValues.itemLookupTypeId === MIXED ? "— Multiple Values —" : "— Select type —"}
                                        />
                                    </Col>
                                </FormGroup>
                                {!isQuoteImport && !showImportTab && (
                                    <FormGroup row className="align-items-center mt-2">
                                        <Label sm={4} className="col-form-label" htmlFor="quoteGroupDescription">
                                            Group Name
                                        </Label>
                                        <Col sm={8}>
                                            <Input
                                                id="quoteGroupDescription"
                                                data-testid="quote-group-description"
                                                type="text"
                                                disabled={containsNonFullyEditableItems}
                                                defaultValue={commonStartValues.quoteGroupDescription !== MIXED ? (commonStartValues.quoteGroupDescription ?? "") : ""}
                                                placeholder={commonStartValues.quoteGroupDescription === MIXED ? "Multiple Values" : ""}
                                                onChange={onText("quoteGroupDescription")}
                                            />
                                        </Col>
                                    </FormGroup>
                                )}
                                {isQuoteImport && (
                                    <>
                                        <FormGroup row className="align-items-center mt-2">
                                            <Label sm={4} className="col-form-label" htmlFor="groupName">
                                                Group
                                            </Label>                                    
                                            <Col sm={8}>
                                                <CustomSelect
                                                    inputId="groupName"
                                                    isSearchable
                                                    options={groupOptions}
                                                    onChange={(opt) => {
                                                        setAction(dispatch, "groupName", opt?.label ?? null);
                                                    }}
                                                    placeholder="— Select group —"
                                                />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row className="align-items-center">
                                            <Col sm={4}></Col>
                                            {!isCreatingGroupName ?                                    
                                                <Col sm={8}>
                                                    <Button onClick={() => {
                                                        setIsCreatingGroupName(true);
                                                        setTimeout(() => newGroupNameInputRef.current?.focus(), 0);
                                                    }}>Create New Group</Button>
                                                </Col>
                                            :
                                                <Col sm={8} className="d-flex align-items-center gap-2">
                                                    <Input 
                                                        innerRef={newGroupNameInputRef}
                                                        type="text"
                                                        value={newGroupName}
                                                        onChange={(e) => setNewGroupName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') createGroupName(); }}
                                                        placeholder="Enter the new group name"
                                                    />
                                                    <Button onClick={createGroupName}>OK</Button>
                                                    <Button outline onClick={() => {
                                                        setNewGroupName("");
                                                        setIsCreatingGroupName(false);
                                                    }}>Cancel</Button>
                                                </Col>
                                        }
                                        </FormGroup>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </>)}

                    {(currentActiveTab === 'Commercials' || currentActiveTab === 'Import') && (<>
                        {/* Pricing calculators */}
                        <Row className="g-5">
                            {/* Upfront column */}
                            <Col xl={6}>
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                    <h5 className="mb-0">Upfront</h5>
                                    <hr style={{ flex: 1, margin: 0 }} />
                                    {allowSetBillingType && <Button
                                            outline
                                            disabled={containsNonFullyEditableItems || numRecurringItems === 0}
                                            onClick={() => {
                                                setBillingAction(dispatch, "upfront");
                                                setAction(dispatch, "isUpfront", true);
                                                setAction(dispatch, "isRecurring", false);
                                            }}
                                        >
                                            Move All Recurring To Upfront
                                        </Button>
                                    }
                                </div>

                                {numUpfrontItems === 0 && (
                                    <div className="mt-4">
                                        <i className="bi bi-info-circle-fill" aria-hidden="true"></i>
                                        <span className="text-muted">There are no items with upfront billing, so you cannot edit upfront fields.</span>
                                    </div>
                                )}

                                {numUpfrontItems > 0 && (
                                    <div className="mt-4">
                                        <i className="bi bi-info-circle-fill me-2" aria-hidden="true"></i>
                                        <span className="text-muted">There {numUpfrontItems === 1 ? "is" : "are"} {numUpfrontItems} item{numUpfrontItems > 1 ? "s" : ""} with upfront billing. Any changes made below will apply to {numUpfrontItems === 1 ? "this item only" : "all of these items"}.</span>
                                    </div>
                                )}                                

                                <Row>
                                    <Col>
                                        {/* Upfront Quote Description */}
                                        <FormGroup className="mt-3">
                                            <Label htmlFor="upfrontDescription">Upfront Quote Description</Label>
                                            <Input
                                                id="upfrontDescription"
                                                type="text"
                                                disabled={numUpfrontItems === 0}
                                                defaultValue={commonStartValues.upfrontDescription !== MIXED ? (commonStartValues.upfrontDescription ?? "") : ""}
                                                placeholder={commonStartValues.upfrontDescription === MIXED ? "Multiple Values" : ""}
                                                onChange={(e) => {
                                                    onText("upfrontDescription")(e);
                                                    onText("upfrontNameOnQuote")(e)
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup className="mt-3">
                                            <Label htmlFor="upfrontSalesforceCode">
                                                Upfront SF Code
                                            </Label>
                                            <CustomSelect
                                                inputId="upfrontSalesforceCode"
                                                options={upfrontSfCodeOptions}
                                                isDisabled={containsNonFullyEditableItems || numUpfrontItems === 0}
                                                value={commonUpfrontSalesforceCode}
                                                onChange={(opt) => {
                                                    setAction(dispatch, "upfrontSalesforceCode", opt?.value ?? null);
                                                }}
                                                placeholder={commonStartValues.upfrontSalesforceCode === MIXED ? "— Multiple Values —" : numUpfrontItems === 0 ? "— No upfront items —" : "— Select a code —"}
                                                styles={{ control: (base) => ({ ...base, minHeight: "auto", height: "calc(1.5em + 0.75rem)" }), valueContainer: (base) => ({ ...base, padding: "0 8px" }), indicatorsContainer: (base) => ({ ...base, height: "calc(1.5em + 0.75rem)" }) }}
                                            />
                                        </FormGroup>   
                                    </Col>
                                    <Col xs="auto">
                                        <FormGroup className="mt-3">
                                            <Label htmlFor="upfrontRecognitionMonths">Recognition Period</Label>
                                            <Input
                                                id="upfrontRecognitionMonths"
                                                type="number"
                                                style={{ maxWidth: "70px" }}
                                                placeholder="Months"
                                                disabled={containsNonFullyEditableItems || numUpfrontItems === 0}
                                                defaultValue={commonStartValues.upfrontRecognitionMonths !== MIXED ? (commonStartValues.upfrontRecognitionMonths ?? "") : ""}
                                                onChange={onNumber("upfrontRecognitionMonths")} />                                                    
                                        </FormGroup>
                                    </Col>
                                </Row>                             

                                <div className="mt-2">
                                    <CalcRow 
                                        label="Upfront Cost" 
                                        field="upfrontCost" 
                                        setField={setField} 
                                        applyCalcOnce={applyCalcOnce} 
                                        isLocked={isCostLocked || numUpfrontItems === 0} 
                                        reset={() => dispatch({ type: "add", payload: { kind: "resetPricing", field: "upfrontCost" } })}
                                    />
                                    <CalcRow 
                                        label="Upfront Price" 
                                        field="upfrontPrice" 
                                        setField={setField} 
                                        applyCalcOnce={applyCalcOnce} 
                                        isLocked={numUpfrontItems === 0}
                                        reset={() => dispatch({ type: "add", payload: { kind: "resetPricing", field: "upfrontPrice" } })} 
                                    />
                                </div>
                            </Col>

                            {/* Recurring column */}
                            <Col xl={6}>
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                    <h5 className="mb-0">Recurring</h5>
                                    <hr style={{ flex: 1, margin: 0 }} />
                                    {allowSetBillingType && <Button
                                            outline
                                            disabled={containsNonFullyEditableItems || numUpfrontItems === 0}
                                            onClick={() => {
                                                setBillingAction(dispatch, "recurring");
                                                setAction(dispatch, "isUpfront", false);
                                                setAction(dispatch, "isRecurring", true);
                                            }}
                                        >
                                            Move All Upfront To Recurring
                                        </Button>
                                    }
                                </div>

                                {numRecurringItems === 0 && (
                                    <div className="mt-4">
                                        <i className="bi bi-info-circle-fill me-2" aria-hidden="true"></i>
                                        <span className="text-muted">There are no items with recurring billing. So you cannot edit recurring fields.</span>
                                    </div>
                                )}

                                {numRecurringItems > 0 && (
                                    <div className="mt-4">
                                        <i className="bi bi-info-circle-fill me-2" aria-hidden="true"></i>
                                        <span className="text-muted">There {numRecurringItems === 1 ? "is" : "are"} {numRecurringItems} item{numRecurringItems > 1 ? "s" : ""} with recurring billing. Any changes made below will apply to {numRecurringItems === 1 ? "this item only" : "all of these items"}.</span>
                                    </div>
                                )}

                                <Row>
                                    <Col>
                                        {/* Recurring Quote Description */}
                                        <FormGroup className="mt-3">
                                            <Label htmlFor="recurringDescription">Recurring Quote Description</Label>
                                            <Input
                                                id="recurringDescription"
                                                type="text"
                                                disabled={numRecurringItems === 0}
                                                defaultValue={commonStartValues.recurringDescription !== MIXED ? (commonStartValues.recurringDescription ?? "") : ""}
                                                placeholder={commonStartValues.recurringDescription === MIXED ? "Multiple Values" : ""}
                                                onChange={(e) => {
                                                    onText("recurringDescription")(e)
                                                    onText("recurringNameOnQuote")(e)
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <FormGroup className="mt-3">
                                            <Label htmlFor="recurringSalesforceCode">
                                                Recurring SF Code
                                            </Label>
                                            <CustomSelect
                                                inputId="recurringSalesforceCode"
                                                options={recurringSfCodeOptions}
                                                isDisabled={containsNonFullyEditableItems || numRecurringItems === 0}
                                                value={commonRecurringSalesforceCode}
                                                onChange={(opt) => {
                                                    setAction(dispatch, "recurringSalesforceCode", opt?.value ?? null);
                                                }}
                                                placeholder={commonStartValues.recurringSalesforceCode === MIXED ? "— Multiple Values —" : numRecurringItems === 0 ? "— No recurring items —" : "— Select a code —"}
                                                styles={{ control: (base) => ({ ...base, minHeight: "auto", height: "calc(1.5em + 0.75rem)" }), valueContainer: (base) => ({ ...base, padding: "0 8px" }), indicatorsContainer: (base) => ({ ...base, height: "calc(1.5em + 0.75rem)" }) }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <div className="mt-2">
                                    <CalcRow 
                                        label="Recurring Cost"
                                        field="recurringCost"
                                        setField={setField}
                                        applyCalcOnce={applyCalcOnce} 
                                        isLocked={isCostLocked || numRecurringItems === 0} 
                                        reset={() => dispatch({ type: "add", payload: { kind: "resetPricing", field: "recurringCost" } })} 
                                    />
                                    <CalcRow
                                        label="Recurring Price"
                                        field="recurringPrice"
                                        setField={setField}
                                        applyCalcOnce={applyCalcOnce}
                                        isLocked={numRecurringItems === 0}
                                        reset={() => dispatch({ type: "add", payload: { kind: "resetPricing", field: "recurringPrice" } })}
                                    />
                                </div>

                                {/* Recurring timing fields */}
                                <Row className="align-items-end g-2 mt-2">
                                    <Col sm={2}>
                                        <Label htmlFor="recurringStartMonth">Start Month</Label>
                                        <Input
                                            id="recurringStartMonth"
                                            type="number"
                                            disabled={containsNonFullyEditableItems || numRecurringItems === 0}
                                            defaultValue={commonStartValues.recurringStartMonth !== MIXED ? (commonStartValues.recurringStartMonth ?? "") : ""}
                                            onChange={onNumber("recurringStartMonth")} />
                                    </Col>
                                    <Col sm={2}>
                                        <Label htmlFor="recurringIntervalMonths">Interval (Months)</Label>
                                        <Input
                                            id="recurringIntervalMonths"
                                            type="number"
                                            disabled={containsNonFullyEditableItems || numRecurringItems === 0}
                                            defaultValue={commonStartValues.recurringIntervalMonths !== MIXED ? (commonStartValues.recurringIntervalMonths ?? "") : ""}
                                            onChange={onNumber("recurringIntervalMonths")}
                                        />
                                    </Col>
                                    <Col sm={2}>
                                        <Label htmlFor="recurringNumPayments">Num Payments</Label>
                                        <Input
                                            id="recurringNumPayments"
                                            type="number"
                                            disabled={containsNonFullyEditableItems || numRecurringItems === 0}
                                            defaultValue={commonStartValues.recurringNumPayments !== MIXED ? (commonStartValues.recurringNumPayments ?? "") : ""}
                                            onChange={onNumber("recurringNumPayments")}
                                        />
                                    </Col>
                                </Row>


                            </Col>
                        </Row>

                        <hr />

                        <Row className="">
                            <Col sm="2">
                                <FormGroup row>
                                    <Label for="isInternal" sm={"auto"}>
                                        Internal
                                    </Label>
                                    <Col sm={1}>
                                        <IndeterminateCheckbox
                                            id="isInternal"
                                            mixed={commonStartValues.isInternal === MIXED}
                                            defaultChecked={commonStartValues.isInternal !== MIXED && !!commonStartValues.isInternal}
                                            onChange={onCheck("isInternal")}
                                            disabled={containsNonFullyEditableItems}
                                            className="mt-2"
                                        />
                                    </Col>
                                </FormGroup>
                            </Col>
                            <Col sm="2">
                                <FormGroup row>
                                    <Label for="isCreditItem" sm={"auto"}>
                                        Credit Item
                                    </Label>
                                    <Col sm={1}>
                                        <IndeterminateCheckbox
                                            id="isCreditItem"
                                            mixed={commonStartValues.isCreditItem === MIXED}
                                            defaultChecked={commonStartValues.isCreditItem !== MIXED && !!commonStartValues.isCreditItem}
                                            onChange={onCheck("isCreditItem")}
                                            disabled={containsNonFullyEditableItems}
                                            className="mt-2"
                                        />
                                    </Col>
                                </FormGroup>
                            </Col>
                        </Row>
                    </>)}

                    {currentActiveTab === 'Configuration' && (
                        <>
                            {groupedItemsByType.map((group) => {
                                const typeKey = group.typeId != null ? String(group.typeId) : String(group.typeName);
                                const defAttrs = (() => {
                                    const ca = group?.def?.customAttributes;
                                    if (!ca) return [];
                                    if (Array.isArray(ca)) return ca;
                                    if (typeof ca === "string") {
                                        try { return JSON.parse(ca); } catch { return []; }
                                    }
                                    return [];
                                })();

                                // find current common values (for a friendlier default)
                                const commonFor = (label) => {
                                    const norm = String(label || "").trim().toLowerCase();
                                    let first = undefined;
                                    for (const r of group.rows) {
                                        const arr = Array.isArray(r.typeCustomAttributes) ? r.typeCustomAttributes : [];
                                        const found = arr.find(a => String(a?.label ?? a?.name ?? "")
                                            .trim()
                                            .toLowerCase() === norm);
                                        const val = found?.value ?? null;
                                        if (first === undefined) first = val;
                                        else if (!same(first, val)) return ""; // not common
                                    }
                                    return first ?? "";
                                };

                                return (
                                    <Collapsable
                                        key={typeKey}
                                        header={`${group.typeName} (${group.rows.length})`}
                                        open={false}
                                        fluid={true}
                                        icon="bi-gear-fill"
                                        color="bg-secondary"
                                    >
                                        {defAttrs.length === 0 && (
                                            <p className="text-muted mb-0">No type attributes are defined for this type.</p>
                                        )}

                                        {defAttrs.map((attr) => {
                                            const label = String(attr?.label ?? attr?.name ?? attr?.key ?? "").trim();
                                            const draftKey = `${typeKey}::${label}`;
                                            const rawDraftVal = configDraft[draftKey] ?? commonFor(label) ?? "";
                                            // what the child control should receive
                                            const valueForControl = isBooleanField(attr.fieldType)
                                                ? fromYesNo(rawDraftVal)   // boolean for checkbox UI (handles Yes/No/true/false/on/off)
                                                : rawDraftVal;

                                            return (
                                                <Row key={draftKey} className="align-items-center g-2 mb-2">
                                                    <Col sm={2}>
                                                        <Label className="mb-0">{label}</Label>
                                                    </Col>
                                                    <Col sm={2}>
                                                        <ItemTypeCustomAttributes
                                                            label={label}
                                                            value={valueForControl}
                                                            handleChange={(e) => {
                                                                let inputVal;
                                                                if (e && e.target) {
                                                                    inputVal = e.target.type === "checkbox" ? e.target.checked : e.target.value;
                                                                } else {
                                                                    inputVal = e; // in case ItemTypeCustomAttributes calls back with a raw value
                                                                }
                                                                // store Yes/No for boolean fields, raw for others
                                                                const stored = isBooleanField(attr.fieldType) ? toYesNo(inputVal) : inputVal;
                                                                setConfigDraft((prev) => ({ ...prev, [draftKey]: stored }));
                                                                dispatch({
                                                                    type: "add",
                                                                    payload: {
                                                                        kind: "setTypeAttr",
                                                                        typeKey,
                                                                        attrLabel: label,
                                                                        value: stored,
                                                                    },
                                                                });
                                                            }}
                                                            lookupChoices={attr.lookupChoices}
                                                            required={attr.required}
                                                            type={attr.fieldType}
                                                        />
                                                    </Col>
                                                </Row>
                                            );
                                        })}
                                    </Collapsable>
                                );
                            })}
                        </>
                    )}

                    <hr />

                    {/* Preview table */}
                    <DataTable columns={columns} data={preview} getCellClassName={getCellClassName} />
                </Container>
            </ModalBody>
            <ModalFooter>
                <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={() => setModalOpen(false)}>
                    Cancel
                </Button>
                <Button
                    color="secondary"
                    onClick={() => {
                        const updated = applyActions(items, actions); // apply all queued actions
                        if (typeof onSave === "function") onSave(updated);
                        setModalOpen(false);
                    }}
                >
                    Update
                </Button>
            </ModalFooter>
        </Modal >
    );
}
